import React from "react";
import { Link } from "react-router-dom";
import {
  Lock,
  ArrowLeft,
  CheckCircle2,
  ShieldCheck,
  Database,
  Server,
  AlertTriangle
} from "lucide-react";

export default function SecurityPage() {
  const items = [
    "Autenticação segura e controle de sessões",
    "Autenticação em dois fatores opcional",
    "Separação lógica de dados por clínica e usuário",
    "Comunicação protegida via HTTPS",
    "Backups administrativos e rotinas de proteção",
    "Controle de permissões e acesso restrito",
    "Ambientes separados de staging e produção",
    "Logs administrativos e rastreabilidade operacional",
    "Alertas administrativos para exportações de prontuários",
    "Proteção contra acessos não autorizados",
    "Criptografia dos tokens de autorização de integrações externas"
  ];

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <header className="border-b bg-white sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-4 flex justify-between items-center">
          <img
            src="/marketing/logo_official.png"
            className="h-14"
            alt="OdontoAssist AI"
          />

          <Link
            to="/"
            className="text-indigo-600 font-semibold flex gap-2"
          >
            <ArrowLeft size={16} />
            Voltar
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12 space-y-12 [&_p]:text-justify [&_li]:text-justify">

        <section>
          <div className="flex items-center gap-3 mb-4">
            <ShieldCheck className="text-indigo-600" size={30} />
            <h1 className="text-4xl font-black">
              Segurança da Informação
            </h1>
          </div>

          <p className="text-gray-600 leading-relaxed">
            O OdontoAssist AI trata segurança da informação como parte essencial
            da arquitetura da plataforma. Como lidamos com dados operacionais,
            administrativos e dados sensíveis de saúde, adotamos medidas técnicas
            e organizacionais voltadas à confidencialidade, integridade,
            disponibilidade e rastreabilidade das informações.
          </p>
        </section>

        <section>
          <h2 className="font-bold text-2xl mb-6">
            Medidas de segurança adotadas
          </h2>

          <div className="grid gap-4">
            {items.map((item, i) => (
              <div
                key={i}
                className="flex gap-3 border border-gray-200 p-4 rounded-xl"
              >
                <CheckCircle2 className="text-indigo-600 shrink-0" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="grid md:grid-cols-2 gap-6">

          <div className="border rounded-2xl p-6">
            <Lock className="text-indigo-600 mb-4" size={26} />
            <h3 className="font-bold text-lg mb-2">
              Autenticação e acesso
            </h3>
            <p className="text-gray-600 leading-relaxed">
              O acesso ao sistema depende de autenticação válida, controle de
              sessão e regras de permissão por perfil de usuário. Cada colaborador
              deve utilizar sua própria conta, e o segundo fator pode ser ativado
              para reforçar a proteção do acesso.
            </p>
          </div>

          <div className="border rounded-2xl p-6">
            <Database className="text-indigo-600 mb-4" size={26} />
            <h3 className="font-bold text-lg mb-2">
              Proteção dos dados
            </h3>
            <p className="text-gray-600 leading-relaxed">
              Os dados de clínicas e pacientes são segregados logicamente,
              reduzindo riscos de exposição indevida entre contas.
            </p>
            <p className="text-gray-600 leading-relaxed mt-3">
              Credenciais OAuth utilizadas em integrações, como o Google Calendar,
              são armazenadas de forma criptografada. A plataforma não recebe nem
              armazena a senha da conta Google conectada.
            </p>
          </div>

          <div className="border rounded-2xl p-6">
            <Server className="text-indigo-600 mb-4" size={26} />
            <h3 className="font-bold text-lg mb-2">
              Infraestrutura
            </h3>
            <p className="text-gray-600 leading-relaxed">
              Utilizamos infraestrutura com ambientes separados para testes e
              produção, reduzindo riscos operacionais durante evoluções do sistema.
            </p>
          </div>

          <div className="border rounded-2xl p-6">
            <ShieldCheck className="text-indigo-600 mb-4" size={26} />
            <h3 className="font-bold text-lg mb-2">
              Monitoramento
            </h3>
            <p className="text-gray-600 leading-relaxed">
              Eventos relevantes podem ser registrados para auditoria,
              rastreabilidade e investigação técnica quando necessário.
            </p>
          </div>

        </section>

        <section className="bg-amber-50 border border-amber-200 rounded-2xl p-8">
          <div className="flex gap-3 items-start">
            <AlertTriangle className="text-amber-600 shrink-0 mt-1" />
            <div>
              <h2 className="font-bold text-xl mb-2">
                Responsabilidade compartilhada
              </h2>

              <p className="text-gray-700 leading-relaxed">
                A segurança também depende das boas práticas adotadas pelo usuário,
                como utilização de senhas fortes, proteção dos dispositivos,
                não compartilhamento de credenciais e encerramento correto das sessões.
              </p>
            </div>
          </div>
        </section>

        <section className="text-sm text-gray-500 border-t pt-6">
          <p>Última atualização: 8 de agosto de 2026.</p>
        </section>

      </main>
    </div>
  );
}
